import React, { useState } from 'react';

/**
 * "Needs attention" strip — turns a dashboard from a mirror into an advisor.
 *
 * A thin band of computed signal chips: each is a one-click jump to the thing
 * that needs action (stalled events, suspended orgs, empty venues, …). Chips
 * are severity-colored and dismissible for the session. If there's nothing to
 * flag, the strip renders a calm "all clear" line instead.
 */

export type AttentionSeverity = 'critical' | 'warning' | 'info';

export interface AttentionSignal {
  id: string;
  severity: AttentionSeverity;
  icon: string;
  text: string;        // e.g. "2 events stalled under 50%"
  onClick: () => void;
}

const SEV: Record<AttentionSeverity, { bg: string; border: string; fg: string; dot: string }> = {
  critical: { bg: '#fef2f2', border: '#fecaca', fg: '#991b1b', dot: '#ef4444' },
  warning: { bg: '#fffbeb', border: '#fde68a', fg: '#92400e', dot: '#f59e0b' },
  info: { bg: '#eff6ff', border: '#bfdbfe', fg: '#1e40af', dot: '#3b82f6' },
};

export function AttentionStrip({ signals }: { signals: AttentionSignal[] }) {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const visible = signals.filter((s) => !dismissed.has(s.id));

  if (signals.length === 0) {
    return (
      <div style={styles.allClear}>
        <span style={styles.allClearDot} /> All clear — nothing needs your attention right now.
      </div>
    );
  }
  if (visible.length === 0) return null;

  // Sort critical → warning → info.
  const order: AttentionSeverity[] = ['critical', 'warning', 'info'];
  const sorted = [...visible].sort((a, b) => order.indexOf(a.severity) - order.indexOf(b.severity));

  return (
    <div style={styles.wrap} aria-label="Needs attention">
      <span style={styles.lead}>⚡ Needs attention</span>
      <div style={styles.chips}>
        {sorted.map((s) => {
          const c = SEV[s.severity];
          return (
            <span key={s.id} style={{ ...styles.chip, background: c.bg, borderColor: c.border, color: c.fg }}>
              <button
                type="button"
                onClick={s.onClick}
                style={{ ...styles.chipBtn, color: c.fg }}
                aria-label={s.text}
              >
                <span style={{ ...styles.chipDot, background: c.dot }} />
                <span style={styles.chipIcon}>{s.icon}</span>
                {s.text}
                <span style={styles.chipArrow}>→</span>
              </button>
              <button
                type="button"
                onClick={() => setDismissed((prev) => new Set(prev).add(s.id))}
                style={{ ...styles.dismiss, color: c.fg }}
                aria-label={`Dismiss: ${s.text}`}
                title="Dismiss"
              >
                ×
              </button>
            </span>
          );
        })}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  wrap: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 22, padding: '12px 14px', borderRadius: 14, background: 'linear-gradient(90deg, #fff, #fafafe)', border: '1px solid #eef0f4' },
  lead: { fontSize: '0.78rem', fontWeight: 800, color: '#334155', whiteSpace: 'nowrap' },
  chips: { display: 'flex', gap: 8, flexWrap: 'wrap', flex: 1 },
  chip: { display: 'inline-flex', alignItems: 'center', border: '1px solid', borderRadius: 999, overflow: 'hidden' },
  chipBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700, padding: '6px 4px 6px 12px' },
  chipDot: { width: 7, height: 7, borderRadius: '50%', display: 'inline-block' },
  chipIcon: { fontSize: '0.85rem' },
  chipArrow: { marginLeft: 2, fontWeight: 800 },
  dismiss: { border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '1rem', fontWeight: 700, padding: '6px 10px 6px 4px', opacity: 0.6, lineHeight: 1 },
  allClear: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 22, padding: '10px 14px', borderRadius: 12, background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#065f46', fontSize: '0.85rem', fontWeight: 600 },
  allClearDot: { width: 8, height: 8, borderRadius: '50%', background: '#10b981', display: 'inline-block' },
};
